import { getCognitoConfig } from '@/config/cognito';
import { ICognitoUserPrincipal } from '@/interface/ICognitoUserPrincipal';
import { INextPageContext } from '@/interface/INextPageContext';
import Auth, { CognitoUser } from '@aws-amplify/auth';
import Amplify from '@aws-amplify/core';
import { CognitoUserSession } from 'amazon-cognito-identity-js';
import moment from 'moment';
import { destroyCookie as serverRemoveCookie, parseCookies, setCookie } from 'nookies';
import { isUndefined } from 'lodash';

//From AWS
interface ICookieStorageData {
    domain: string;
    path?: string;
    expires?: number;
    secure?: boolean;
}

let isClientConfigured = false;
let isServerConfigured = false

class TokenCookieStorage {

    private readonly context: INextPageContext | undefined;
    private readonly cookieConfig: ICookieStorageData;

    constructor(nextContext: INextPageContext, option: ICookieStorageData) {
        this.context = nextContext;
        this.cookieConfig = option;
    }

    public setItem(key: string, value: string): void {
        const config = {
            ...this.cookieConfig,
            expires: moment().add(this.cookieConfig.expires, 'days').toDate()
        }

        if (typeof document !== 'undefined') {
            if (window.navigator.userAgent.indexOf('Edge') != -1) {
                if (config.domain == 'localhost')
                    delete config.domain;
            }
        }

        setCookie(this.context, key, value, config);
    }

    public getItem(key: string): string {
        return parseCookies(this.context)[key];
    }

    public removeItem(key: string): void {
        if (typeof document !== 'undefined') {
            let delString = `${key}=;expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${this.cookieConfig.path}; `;

            if (!(window.navigator.userAgent.indexOf('Edge') != -1 && this.cookieConfig.domain == 'localhost'))
                delString += `domain=${this.cookieConfig.domain};`;
            document.cookie = delString;
        }
        else
            serverRemoveCookie(this.context?.isServer ? this.context : {} as any, key);
    }

    public clear(): void {
        Object
            .keys(parseCookies(this.context))
            .forEach(key => this.removeItem(key));
    }
}

/**
 * Execute this function in getInitialProps
 * Please don't execute this function in _app.js - getInitialProps
 * Ref: https://github.com/zeit/next.js/blob/master/errors/opt-out-auto-static-optimization.md
 * @param nextContext 
 */
export function configureCognito(configure: Record<string, string>, nextContext?: INextPageContext): void {  
    const { cookieStorage, ...rest } = getCognitoConfig(configure);

    const CookieStorageInstance = new TokenCookieStorage(nextContext, cookieStorage)
    const options = {
        ...rest,
        storage: CookieStorageInstance
    };
    Amplify.configure(options);

    if(isUndefined(nextContext))
        isClientConfigured = true;
    else
        isServerConfigured = true;    
}

export function isConfigured (): boolean {
    return isClientConfigured || isServerConfigured;
}

export async function isLoggedIn(nextContext: INextPageContext): Promise<boolean> {
    try {
        // *** IMPORTANT *** No need to trigger auth in server side
        if (nextContext?.isServer) {
            return;
        }
        await Auth.currentAuthenticatedUser();
        return true;
    }
    catch (error) {
        return false;
    }
}

export function userPrincipalParser(userSession: CognitoUserSession): ICognitoUserPrincipal {
    const payload = userSession.getIdToken().payload;
    return {
        ...payload,
        userId: payload.sub,
        funderId: payload.funderId,
        groups: payload['cognito:groups'] || []
    } as ICognitoUserPrincipal
}

export async function signOut(cb: () => void): Promise<void> {
    await Auth.signOut();
    if (cb && cb.constructor && cb.constructor.name == 'AsyncFunction')
        await cb();
    else if (typeof cb === 'function')
        cb();
}

interface IUpdatePassword {
    currentPassword: string;
    newPassword: string;
    onSuccess: () => void;
    onReject: (error?: Error) => void;
}

export async function updatePassword(data: IUpdatePassword): Promise<void> {
    await Auth
        .changePassword(
            await Auth.currentAuthenticatedUser(),
            data.currentPassword,
            data.newPassword
        )
        .then(data.onSuccess)
        .catch((error) => {
            console.error(error);
            data.onReject(error);
        });
}

export async function refreshSession(cb?: (error: Error, result: any) => void): Promise<void> {
    const user = await Auth.currentAuthenticatedUser() as CognitoUser;
    if (user) {
        return new Promise((resolve, reject) => {
            user.refreshSession(
                user.getSignInUserSession().getRefreshToken(),
                (error, result) => {
                    if (cb) {
                        reject(cb(error, result));
                    }
                    if (error) {
                        reject(error);
                    }
                    resolve(result);
                }
            );
        });
    }
}

export async function updateLocale(locale: 'en_US' | 'zh_TW'): Promise<void> {
    await Auth.updateUserAttributes(
        await Auth.currentAuthenticatedUser(),
        { locale }
    )
        .then(() => refreshSession());
}
